import { Injectable, inject } from '@angular/core';
import { OptionsCacheService } from './optionsCache.service';
import { firstValueFrom } from 'rxjs';
import { Apollo } from 'apollo-angular';
import { GET_TABLE_VERSION, GET_TABLE_VERSIONS } from 'app/graphql/versions';

interface TableVersion {
  tableName: string;
  version: number;
}

interface TableVersionsResponse {
  tableVersions: TableVersion[];
}

interface VersionResponse {
  version: number;
}

@Injectable({
  providedIn: 'root'
})
export class CachedOptionsService {
  private cacheService = inject(OptionsCacheService);
  private apollo = inject(Apollo);

  async getCachedData<T = any>(options: {
    query: any;
    variables?: any;
    dataPath: string;
    tableName: string;
    versionTableName?: string;
    fetchPolicy?: 'cache-first' | 'network-only' | 'cache-only' | 'no-cache' | 'standby';
  }): Promise<T[]> {
    const {
      query,
      variables = {},
      dataPath,
      tableName,
      versionTableName = tableName,
      fetchPolicy = 'cache-first'
    } = options;

    if (!tableName) {
      console.error('getCachedData called with empty tableName!', options);
      console.trace();
    }


    return this.cacheService.getOptions<T>(
      tableName,
      async () => {
        const result = await firstValueFrom(
          this.apollo.query({
            query,
            variables,
            fetchPolicy
          })
        );
        // Accede a los datos usando la ruta proporcionada
        return this.getNestedValue(result.data, dataPath) || [];
      },
      versionTableName ? () => this.getTableVersion(versionTableName) : undefined
    );
  }

  // Método auxiliar para acceder a propiedades anidadas
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((o, p) => o?.[p], obj);
  }

  // Método para obtener la versión de una tabla específica
  async getTableVersion(tableName: string): Promise<number> {
    try {
      const result = await firstValueFrom(
        this.apollo.query<VersionResponse>({
          query: GET_TABLE_VERSION,
          variables: { tableName },
          fetchPolicy: 'network-only'
        })
      );

      // TypeScript ahora sabe que result.data tiene la forma de VersionResponse
      return result.data?.version || 0;
    } catch (error) {
      console.error('Error getting table version:', error);
      return 0;
    }
  }

  async loadAllVersions(): Promise<TableVersion[]> {
    return this.cacheService.getOptions<TableVersion>(
      'allTableVersions', // Clave única para este conjunto de datos
      async () => {
        const result = await firstValueFrom(
          this.apollo.query<TableVersionsResponse>({
            query: GET_TABLE_VERSIONS,
            fetchPolicy: 'network-only'
          })
        );
        return result.data.tableVersions;
      },
      async () => {
        // Usamos una versión fija para la caché de todas las versiones
        // ya que no tiene sentido verificar la versión de las versiones
        return 1;
      }
    );
  }
  //   async loadAllVersions(): Promise<TableVersion[]> {
  //     try {
  //       const result = await firstValueFrom(
  //         this.apollo.query<TableVersionsResponse>({
  //           query: GET_TABLE_VERSIONS,
  //           fetchPolicy: 'network-only'
  //         })
  //       );
  //       console.log('Todas las versiones:', result.data.tableVersions);
  //       return result.data.tableVersions;
  //     } catch (error) {
  //       console.error('Error cargando versiones:', error);
  //       return [];
  //     }
  //   }
}